"use client";

import { memo } from "react";
import { cn } from "@/lib/utils";
import {
  GOOGLE_REVIEW_URL,
  TRUSTPILOT_RATING_DISPLAY,
  TRUSTPILOT_REVIEW_URL,
} from "@/components/screen/start-order/components/Checkout/constants";

/* ============================================================================
 * CredentialsStrip — Component 11 (production drop-in)
 *
 * Source spec: docs / Claude design brief
 *   `quality-trust-section/component-11-cursor-brief.md`
 *
 * Horizontal strip with five trust signals. The Trustpilot/Google review card
 * sits dead center as the focal point, flanked by two credentials on each side:
 *
 *   [USCIS Accepted] [ATA Corporate Member] [TP/GOOGLE BOX] [BBB A+ Rated] [NOTARIZED 50 States]
 *
 * First integration target: the Certified Translation page (/certified-translation).
 * Designed to be reusable — after Imran approves, this same component will
 * roll out to Notarized Translation and any USCIS-specific landing pages.
 *
 * Responsive (per brief):
 *   >=1024px (lg+)         -> 5-item horizontal row, ~140-150px tall, gap-9
 *   768-1023px (md->lg-)   -> same horizontal layout, tighter padding/gaps
 *   <768px (sm and below)  -> 2-col grid; review box spans both cols on top,
 *                             USCIS+ATA on row 2, BBB+NOTARIZED on row 3
 *
 * Claim accuracy (audited against the live codebase before publish):
 *   USCIS Accepted        -> already on OrderSummary trust strip + service pages
 *   ATA Corporate Member  -> already in ServicesPromises copy ("ATA-certified
 *                            translator", "ATA-certified native speakers")
 *   NOTARIZED 50 States   -> already verbatim in checkout OptionsForm ("valid
 *                            in all 50 states")
 *   BBB A+ Rated          -> CONFIRMED by Imran 2026-05-26 (Path A on the
 *                            audit memo). First time the BBB seal lands on
 *                            site so if BBB status ever changes (rating
 *                            downgrade, accreditation lapse, etc.), gate this
 *                            badge in the consumer or rip the strip until
 *                            re-verified — the official seal is regulated.
 *
 * Tokens (all exact matches against existing design system):
 *   strip bg     = bg-background-secondary  (#F7F7F9)
 *   card bg      = bg-white
 *   soft border  = border-gray-100         (#D7DADC)
 *   default brdr = border-neutral-200      (#BCC2C4)
 *   primary text = text-semantic-text-primary (#0B0E10)
 *   subtle text  = text-gray-500           (#58666C)
 *   notary green = bg-green-500            (#2B9057)
 * Institutional + brand colors stay as arbitrary hex on purpose:
 *   USCIS blue   #04478E
 *   ATA navy     #113269
 *   BBB blues    #032160, #0077BF, #0055CA (untouched inside the seal SVG)
 *   Trustpilot   #00B67A
 *   Google G     #4285F4 / #34A853 / #FBBC05 / #EA4335
 *
 * Click behavior on the review box (matches the home TrustStrip pattern):
 *   Trustpilot footer mark -> TRUSTPILOT_REVIEW_URL
 *   Google footer mark     -> GOOGLE_REVIEW_URL (degrades to non-link span
 *                             if the URL gets unset in constants)
 *
 * BBB SVG: kept inline per brief Option A. ~3KB of paths but no extra HTTP
 * request, and currentColor wouldn't work on <img>-loaded SVG anyway (the
 * seal has fixed brand colors so currentColor isn't needed here).
 * ========================================================================== */

const TrustpilotStar = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <polygon
      fill="currentColor"
      points="12,2 15,9 22,9.5 17,14.5 18.5,22 12,18 5.5,22 7,14.5 2,9.5 9,9"
    />
  </svg>
);

const GoogleGlyph = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Shared wrapper for the four credential badges (USCIS / ATA / BBB / Notary).
// Locks in the vertical-stack layout, hover-lift transition, and centered
// alignment so each individual badge body can focus on its own mark + label.
const CredBadge = ({
  children,
  title,
  className,
}: {
  children: React.ReactNode;
  title: string;
  className?: string;
}) => (
  <div
    title={title}
    className={cn(
      "flex flex-col items-center gap-2 text-center transition-transform duration-200 ease-out hover:-translate-y-[2px]",
      className
    )}
  >
    {children}
  </div>
);

// Shared label slot — uppercase 10/11px stacked text under each badge mark.
// `head` renders the bold first line (e.g. "USCIS"), `sub` renders the muted
// second line (e.g. "Accepted").
const CredLabel = ({ head, sub }: { head: string; sub: string }) => (
  <span className="whitespace-nowrap text-[11px] font-medium uppercase leading-[1.3] tracking-[0.05em] text-gray-500 md:text-[10px]">
    <strong className="block text-[12px] font-semibold text-semantic-text-primary md:text-[11px]">
      {head}
    </strong>
    {sub}
  </span>
);

// Standard 38/44px circle holding a small icon for USCIS / ATA / Notary.
// BBB uses its own larger sizing (full official seal SVG), so it does NOT
// route through this wrapper.
const CredCircle = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "relative flex items-center justify-center rounded-full text-white",
      "size-[44px] md:size-[38px]",
      className
    )}
  >
    {children}
  </div>
);

const CredentialsStrip = () => {
  const ratingLabel = `${TRUSTPILOT_RATING_DISPLAY} out of 5 stars`;
  return (
    <section
      aria-label="Trust signals — credentials and customer reviews"
      className="section bg-background-secondary py-7 md:py-5 lg:py-[22px] antialiased"
    >
      <div className="container mx-auto">
        <div
          className={cn(
            // Mobile: 2-column grid. Review box on top spans both cols,
            // then USCIS+ATA row 2, BBB+NOTARIZED row 3.
            "grid grid-cols-2 items-center justify-items-center gap-y-7 gap-x-4",
            // md+: 5-element horizontal row, space-around, single line
            "md:flex md:flex-row md:items-center md:justify-around md:gap-6",
            "lg:gap-9"
          )}
        >
          {/* USCIS — order-2 on mobile so it lands after the box */}
          <CredBadge
            title="USCIS Acceptance Guaranteed"
            className="order-2 md:order-none"
          >
            <CredCircle className="bg-[#04478E]">
              {/* Inner subtle white ring per brief — `inset: 4px` */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-[5px] rounded-full border border-white/45 md:inset-[4px]"
              />
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="relative z-10 size-[24px] md:size-[20px]"
              >
                <polyline points="4 12 10 18 20 6" />
              </svg>
            </CredCircle>
            <CredLabel head="USCIS" sub="Accepted" />
          </CredBadge>

          {/* ATA Corporate Member — order-3 on mobile */}
          <CredBadge
            title="ATA Corporate Member"
            className="order-3 md:order-none"
          >
            <CredCircle className="bg-[#113269]">
              <span
                aria-hidden="true"
                className="text-[11px] font-bold tracking-[0.12em] md:text-[10px]"
              >
                ATA
              </span>
            </CredCircle>
            <CredLabel head="ATA" sub="Corporate Member" />
          </CredBadge>

          {/* CENTER — Trustpilot/Google review box. Spans both grid cols
              on mobile and sits at the top via order-1 + grid-column. */}
          <div
            className={cn(
              "order-1 col-span-2 flex shrink-0 flex-col items-center gap-[6px] rounded-[16px] border border-gray-100 bg-white text-center md:order-none md:col-span-1 md:rounded-[14px]",
              // Drop shadow per brief — depth signals "this is the focal point"
              "shadow-[0_14px_30px_-14px_rgba(11,14,16,0.16),0_2px_6px_-2px_rgba(11,14,16,0.06)]",
              // Sizing — bigger on mobile to dominate; tighter on desktop
              "w-full max-w-[320px] min-w-[240px] px-[22px] py-[14px]",
              "md:w-auto md:max-w-none md:min-w-[200px] md:px-[20px] md:py-[12px]"
            )}
          >
            {/* "Excellent" + 5 stars */}
            <div className="flex items-center justify-center gap-[9px]">
              <h3 className="m-0 text-[14px] font-semibold leading-none text-semantic-text-primary md:text-[12px]">
                Excellent
              </h3>
              <div
                className="inline-flex gap-[2px]"
                aria-label={ratingLabel}
                role="img"
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center justify-center rounded-[3px] bg-[#00B67A] size-[24px] md:size-[20px]"
                  >
                    <TrustpilotStar className="size-[15px] text-white md:size-[13px]" />
                  </span>
                ))}
              </div>
            </div>

            {/* Rating line — "4.9 out of 5" */}
            <p className="m-0 text-[13px] leading-[1.3] text-gray-500 md:text-[11px]">
              <strong className="font-semibold text-semantic-text-primary">
                {TRUSTPILOT_RATING_DISPLAY} out of 5
              </strong>
            </p>

            {/* Dual-platform footer — Trustpilot + Google */}
            <div className="inline-flex items-center border-t border-gray-100 pt-[10px] text-[12px] font-semibold text-semantic-text-primary md:pt-[8px] md:text-[11px]">
              <a
                href={TRUSTPILOT_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[4px] transition-opacity hover:opacity-80"
                aria-label="View Languex reviews on Trustpilot"
              >
                <span className="inline-flex items-center justify-center rounded-[2px] bg-[#00B67A] size-[12px]">
                  <TrustpilotStar className="size-[8px] text-white" />
                </span>
                Trustpilot
              </a>

              <span
                aria-hidden="true"
                className="mx-[7px] inline-block h-[11px] w-px bg-neutral-200"
              />

              {GOOGLE_REVIEW_URL ? (
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-[4px] transition-opacity hover:opacity-80"
                  aria-label="View Languex reviews on Google"
                >
                  <GoogleGlyph className="size-[12px]" />
                  Google
                </a>
              ) : (
                // Defensive fallback — GOOGLE_REVIEW_URL is nullable in the
                // constants contract. If it ever goes null we still want
                // the Google mark visible alongside Trustpilot, just inert.
                <span className="inline-flex items-center gap-[4px]">
                  <GoogleGlyph className="size-[12px]" />
                  Google
                </span>
              )}
            </div>
          </div>

          {/* BBB A+ Rated — order-4 on mobile */}
          <CredBadge
            title="BBB A+ Rated Business"
            className="order-4 md:order-none"
          >
            {/* BBB seal does NOT use CredCircle — it's the full official
                accredited-business mark with its own internal layout +
                fixed brand colors. Render as a row-oriented block at a
                fixed height; SVG aspect ratio (~2.65:1) drives the width. */}
            <div className="flex h-[56px] items-center md:h-[50px]">
              <svg
                viewBox="0 0 323.19 122.12"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="BBB Accredited Business A+ Rating"
                className="block h-full w-auto"
              >
                <path
                  fill="#032160"
                  d="M79.75,6.53h0s0,0,0,0c-30.07,0-54.53,24.46-54.53,54.53s24.46,54.53,54.53,54.53h163.69V6.53H79.75Z"
                />
                <g fill="#FFFFFF">
                  <path d="M86.81,45.35v-13.7h6.43c2.44,0,3.86,1.5,3.86,3.49,0,1.73-1.13,2.88-2.38,3.14,1.46.23,2.65,1.71,2.65,3.37,0,2.16-1.44,3.7-3.96,3.7h-6.59ZM88.86,37.47h3.98c1.38,0,2.14-.86,2.14-1.99s-.76-2.01-2.14-2.01h-3.98v4.01ZM88.86,43.55h4.09c1.44,0,2.3-.82,2.3-2.16,0-1.13-.78-2.12-2.3-2.12h-4.09v4.27Z" />
                  <path d="M99.91,45.35v-13.7h9.2v1.81h-7.15v4.01h7v1.81h-7v4.27h7.15v1.81h-9.2Z" />
                  <path d="M115.02,45.35v-11.89h-4.25v-1.81h10.54v1.81h-4.23v11.89h-2.05Z" />
                  <path d="M126.79,45.35v-11.89h-4.25v-1.81h10.54v1.81h-4.23v11.89h-2.05Z" />
                  <path d="M135.21,45.35v-13.7h9.2v1.81h-7.15v4.01h7v1.81h-7v4.27h7.15v1.81h-9.2Z" />
                  <path d="M154.86,45.35l-3.27-5.28h-2.57v5.28h-2.05v-13.7h5.77c2.61,0,4.39,1.68,4.39,4.21s-1.66,3.78-3.41,4.01l3.51,5.48h-2.38ZM152.48,33.46h-3.45v4.81h3.45c1.48,0,2.55-.97,2.55-2.4s-1.07-2.4-2.55-2.4Z" />
                  <path d="M86.81,63.09v-13.7h6.43c2.44,0,3.86,1.5,3.86,3.49,0,1.73-1.13,2.88-2.38,3.14,1.46.23,2.65,1.71,2.65,3.37,0,2.16-1.44,3.7-3.96,3.7h-6.59ZM88.86,55.2h3.98c1.38,0,2.14-.86,2.14-1.99s-.76-2.01-2.14-2.01h-3.98v4.01ZM88.86,61.28h4.09c1.44,0,2.3-.82,2.3-2.16,0-1.13-.78-2.12-2.3-2.12h-4.09v4.27Z" />
                  <path d="M99.09,49.39h2.07v8.28c0,2.34,1.29,3.84,3.7,3.84s3.68-1.5,3.68-3.84v-8.28h2.07v8.32c0,3.41-1.91,5.63-5.75,5.63s-5.77-2.24-5.77-5.61v-8.34Z" />
                  <path d="M113.17,59.58c.92,1.03,2.4,1.93,4.31,1.93,2.22,0,3-1.13,3-2.12,0-1.44-1.56-1.85-3.31-2.3-2.2-.58-4.74-1.21-4.74-4.01,0-2.26,1.99-3.9,4.85-3.9,2.12,0,3.78.68,4.99,1.91l-1.19,1.52c-1.05-1.11-2.49-1.6-3.96-1.6s-2.57.78-2.57,1.93c0,1.23,1.48,1.62,3.18,2.05,2.24.6,4.85,1.29,4.85,4.21,0,2.14-1.48,4.13-5.18,4.13-2.44,0-4.23-.88-5.4-2.18l1.17-1.58Z" />
                  <path d="M124.14,63.09v-13.7h2.05v13.7h-2.05Z" />
                  <path d="M138.06,63.09l-7.58-10.52v10.52h-2.05v-13.7h2.12l7.46,10.27v-10.27h2.05v13.7h-1.99Z" />
                  <path d="M142.27,63.09v-13.7h9.2v1.81h-7.15v4.01h7v1.81h-7v4.27h7.15v1.81h-9.2Z" />
                  <path d="M153.54,59.58c.92,1.03,2.4,1.93,4.31,1.93,2.22,0,3-1.13,3-2.12,0-1.44-1.56-1.85-3.31-2.3-2.2-.58-4.74-1.21-4.74-4.01,0-2.26,1.99-3.9,4.85-3.9,2.12,0,3.78.68,4.99,1.91l-1.19,1.52c-1.05-1.11-2.49-1.6-3.96-1.6s-2.57.78-2.57,1.93c0,1.23,1.48,1.62,3.18,2.05,2.24.6,4.85,1.29,4.85,4.21,0,2.14-1.48,4.13-5.18,4.13-2.44,0-4.23-.88-5.4-2.18l1.17-1.58Z" />
                  <path d="M164.84,59.58c.92,1.03,2.4,1.93,4.31,1.93,2.22,0,3-1.13,3-2.12,0-1.44-1.56-1.85-3.31-2.3-2.2-.58-4.74-1.21-4.74-4.01,0-2.26,1.99-3.9,4.85-3.9,2.12,0,3.78.68,4.99,1.91l-1.19,1.52c-1.05-1.11-2.49-1.6-3.96-1.6s-2.57.78-2.57,1.93c0,1.23,1.48,1.62,3.18,2.05,2.24.6,4.85,1.29,4.85,4.21,0,2.14-1.48,4.13-5.18,4.13-2.44,0-4.23-.88-5.4-2.18l1.17-1.58Z" />
                  <path d="M86.81,80.83v-13.7h6.43c2.44,0,3.86,1.5,3.86,3.49,0,1.73-1.13,2.88-2.38,3.14,1.46.23,2.65,1.71,2.65,3.37,0,2.16-1.44,3.7-3.96,3.7h-6.59ZM88.86,72.94h3.98c1.38,0,2.14-.86,2.14-1.99s-.76-2.01-2.14-2.01h-3.98v4.01ZM88.86,79.02h4.09c1.44,0,2.3-.82,2.3-2.16,0-1.13-.78-2.12-2.3-2.12h-4.09v4.27Z" />
                  <path d="M99.91,67.13h2.07v8.28c0,2.34,1.29,3.84,3.7,3.84s3.68-1.5,3.68-3.84v-8.28h2.07v8.32c0,3.41-1.91,5.63-5.75,5.63s-5.77-2.24-5.77-5.61v-8.34Z" />
                  <path d="M122.37,80.83l-3.27-5.28h-2.57v5.28h-2.05v-13.7h5.77c2.61,0,4.39,1.68,4.39,4.21s-1.66,3.78-3.41,4.01l3.51,5.48h-2.38ZM119.99,68.94h-3.45v4.81h3.45c1.48,0,2.55-.97,2.55-2.4s-1.07-2.4-2.55-2.4Z" />
                  <path d="M127.18,80.83v-13.7h9.2v1.81h-7.15v4.01h7v1.81h-7v4.27h7.15v1.81h-9.2Z" />
                  <path d="M148.72,80.83l-1.11-2.83h-6.72l-1.11,2.83h-2.26l5.44-13.7h2.57l5.44,13.7h-2.26ZM144.24,68.92l-2.75,7.25h5.52l-2.77-7.25Z" />
                  <path d="M152.04,67.13h2.07v8.28c0,2.34,1.29,3.84,3.7,3.84s3.68-1.5,3.68-3.84v-8.28h2.07v8.32c0,3.41-1.91,5.63-5.75,5.63s-5.77-2.24-5.77-5.61v-8.34Z" />
                  <path d="M90.72,92.56l-.38-.98h-2.32l-.38.98h-.78l1.88-4.74h.89l1.88,4.74h-.78ZM89.17,88.44l-.95,2.51h1.91l-.96-2.51Z" />
                  <path d="M91.61,90.19c0-1.46,1.08-2.45,2.43-2.45.93,0,1.51.45,1.88,1l-.6.32c-.25-.4-.72-.69-1.28-.69-.96,0-1.7.75-1.7,1.82s.74,1.82,1.7,1.82c.56,0,1.03-.29,1.28-.69l.6.32c-.38.55-.95,1-1.88,1-1.35,0-2.43-.99-2.43-2.45Z" />
                  <path d="M96.44,90.19c0-1.46,1.08-2.45,2.43-2.45.93,0,1.51.45,1.88,1l-.6.32c-.25-.4-.72-.69-1.28-.69-.96,0-1.7.75-1.7,1.82s.74,1.82,1.7,1.82c.56,0,1.03-.29,1.28-.69l.6.32c-.38.55-.95,1-1.88,1-1.35,0-2.43-.99-2.43-2.45Z" />
                  <path d="M104.19,92.56l-1.13-1.83h-.89v1.83h-.71v-4.74h2c.9,0,1.52.58,1.52,1.46s-.58,1.31-1.18,1.39l1.22,1.9h-.82ZM103.37,88.45h-1.19v1.66h1.19c.51,0,.88-.33.88-.83s-.37-.83-.88-.83Z" />
                  <path d="M105.85,92.56v-4.74h3.18v.63h-2.47v1.39h2.42v.63h-2.42v1.48h2.47v.63h-3.18Z" />
                  <path d="M109.92,92.56v-4.74h1.69c1.48,0,2.46,1.01,2.46,2.37s-.98,2.37-2.46,2.37h-1.69ZM110.64,91.94h.98c1.09,0,1.73-.78,1.73-1.74s-.61-1.75-1.73-1.75h-.98v3.49Z" />
                  <path d="M114.94,92.56v-4.74h.71v4.74h-.71Z" />
                  <path d="M117.86,92.56v-4.11h-1.47v-.63h3.65v.63h-1.46v4.11h-.71Z" />
                  <path d="M120.78,92.56v-4.74h3.18v.63h-2.47v1.39h2.42v.63h-2.42v1.48h2.47v.63h-3.18Z" />
                  <path d="M124.85,92.56v-4.74h1.69c1.48,0,2.46,1.01,2.46,2.37s-.98,2.37-2.46,2.37h-1.69ZM125.56,91.94h.98c1.09,0,1.73-.78,1.73-1.74s-.61-1.75-1.73-1.75h-.98v3.49Z" />
                  <path d="M131.69,92.56v-4.74h2.22c.85,0,1.34.52,1.34,1.21,0,.6-.39.99-.82,1.09.5.08.92.59.92,1.17,0,.75-.5,1.28-1.37,1.28h-2.28ZM132.4,89.83h1.38c.48,0,.74-.3.74-.69s-.26-.7-.74-.7h-1.38v1.39ZM132.4,91.94h1.41c.5,0,.8-.28.8-.75,0-.39-.27-.73-.8-.73h-1.41v1.48Z" />
                  <path d="M136.23,87.82h.72v2.86c0,.81.45,1.33,1.28,1.33s1.27-.52,1.27-1.33v-2.86h.72v2.88c0,1.18-.66,1.95-1.99,1.95s-2-.77-2-1.94v-2.89Z" />
                  <path d="M141.39,91.35c.32.36.83.67,1.49.67.77,0,1.04-.39,1.04-.73,0-.5-.54-.64-1.15-.8-.76-.2-1.64-.42-1.64-1.39,0-.78.69-1.35,1.68-1.35.73,0,1.31.23,1.73.66l-.41.53c-.36-.38-.86-.55-1.37-.55s-.89.27-.89.67c0,.43.51.56,1.1.71.77.21,1.68.45,1.68,1.46,0,.74-.51,1.43-1.79,1.43-.85,0-1.46-.31-1.87-.75l.4-.55Z" />
                  <path d="M145.47,92.56v-4.74h.71v4.74h-.71Z" />
                  <path d="M150.57,92.56l-2.62-3.64v3.64h-.71v-4.74h.73l2.58,3.55v-3.55h.71v4.74h-.69Z" />
                  <path d="M152.31,92.56v-4.74h3.18v.63h-2.47v1.39h2.42v.63h-2.42v1.48h2.47v.63h-3.18Z" />
                  <path d="M156.49,91.35c.32.36.83.67,1.49.67.77,0,1.04-.39,1.04-.73,0-.5-.54-.64-1.15-.8-.76-.2-1.64-.42-1.64-1.39,0-.78.69-1.35,1.68-1.35.73,0,1.31.23,1.73.66l-.41.53c-.36-.38-.86-.55-1.37-.55s-.89.27-.89.67c0,.43.51.56,1.1.71.77.21,1.68.45,1.68,1.46,0,.74-.51,1.43-1.79,1.43-.85,0-1.46-.31-1.87-.75l.4-.55Z" />
                  <path d="M160.69,91.35c.32.36.83.67,1.49.67.77,0,1.04-.39,1.04-.73,0-.5-.54-.64-1.15-.8-.76-.2-1.64-.42-1.64-1.39,0-.78.69-1.35,1.68-1.35.73,0,1.31.23,1.73.66l-.41.53c-.36-.38-.86-.55-1.37-.55s-.89.27-.89.67c0,.43.51.56,1.1.71.77.21,1.68.45,1.68,1.46,0,.74-.51,1.43-1.79,1.43-.85,0-1.46-.31-1.87-.75l.4-.55Z" />
                </g>
                <g fill="#0077BF">
                  <path d="M75.01,37.44l1.89,1.84c1.89,1.89,2.87,4.48,2.87,7.06s-.98,5.11-2.87,7l-7.06,7.06-3.56-3.5c2.47-2.47,4.88-4.94,7.23-7.29,1.95-1.95,2.93-4.48,2.93-7.06,0-1.78-.46-3.56-1.44-5.11h0Z" />
                  <path d="M67.44,31.47l1.84,1.84c1.95,1.95,2.93,4.54,2.93,7.06s-.98,5.11-2.93,7.06l-3.5,3.5h0l-2.41,2.41h0l-.29.29-.29-.29c-1.95-1.89-2.93-4.48-2.93-7s.98-5.17,2.93-7.06l1.21-1.26c-.63,1.38-.98,2.87-.98,4.36,0,1.21.23,2.41.69,3.56l2.24-2.24c1.95-1.95,2.93-4.54,2.93-7.06,0-1.78-.46-3.56-1.44-5.17h0Z" />
                  <rect
                    x="70.35"
                    y="61.78"
                    width="4.14"
                    height="19.35"
                    rx=".38"
                    ry=".38"
                  />
                  <rect
                    x="65.15"
                    y="66.57"
                    width="4.14"
                    height="14.56"
                    rx=".38"
                    ry=".38"
                  />
                  <rect
                    x="67.75"
                    y="46.12"
                    width="4.14"
                    height="24.94"
                    rx=".5"
                    ry=".5"
                    transform="translate(128.41 -11.22) rotate(90)"
                  />
                </g>
                <path
                  fill="#0055CA"
                  d="M243.44,6.53c30.07,0,54.53,24.46,54.53,54.53s-24.46,54.53-54.53,54.53-54.53-24.46-54.53-54.53S213.38,6.53,243.44,6.53Z"
                />
                <g fill="#FFFFFF">
                  <path d="M262.81,42v7.26h-3.11v-7.26h-6.44v-2.83h6.44v-6.98h3.11v6.98h6.41v2.83h-6.41Z" />
                  <path d="M222.66,87.93l-1.94-3.26h-1.52v3.26h-1.53v-8.72h3.83c1.73,0,2.85,1.12,2.85,2.73s-1.02,2.39-2.07,2.56l2.13,3.43h-1.77ZM221.3,80.55h-2.09v2.77h2.09c.86,0,1.49-.55,1.49-1.39s-.63-1.39-1.49-1.39Z" />
                  <path d="M232.06,87.93l-.64-1.67h-4.13l-.64,1.67h-1.67l3.43-8.72h1.91l3.43,8.72h-1.67ZM229.35,80.5l-1.64,4.41h3.27l-1.63-4.41Z" />
                  <path d="M235.92,87.93v-7.38h-2.64v-1.35h6.81v1.35h-2.64v7.38h-1.53Z" />
                  <path d="M241.38,87.93v-8.72h1.53v8.72h-1.53Z" />
                  <path d="M250.85,87.93l-4.55-6.36v6.36h-1.53v-8.72h1.57l4.46,6.19v-6.19h1.53v8.72h-1.48Z" />
                  <path d="M258.37,79.06c1.7,0,2.81.81,3.52,1.82l-1.26.71c-.46-.65-1.28-1.16-2.26-1.16-1.73,0-3.01,1.32-3.01,3.15s1.28,3.17,3.01,3.17c.89,0,1.67-.42,2.07-.78v-1.36h-2.6v-1.35h4.13v3.27c-.86.95-2.08,1.58-3.6,1.58-2.52,0-4.58-1.79-4.58-4.53s2.05-4.51,4.58-4.51Z" />
                  <path d="M252.03,74.72l-2.68-7.33h-19l-2.68,7.33h-9.95l16.45-42.52h11.35l16.38,42.52h-9.88ZM239.85,40.04l-7.08,19.51h14.09l-7.01-19.51Z" />
                </g>
              </svg>
            </div>
            <CredLabel head="BBB" sub="A+ Rated" />
          </CredBadge>

          {/* Notarized — order-5 on mobile */}
          <CredBadge
            title="Notarized translations accepted in all 50 states"
            className="order-5 md:order-none"
          >
            <CredCircle className="bg-green-500">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="size-[26px] md:size-[22px]"
              >
                <circle cx="12" cy="8" r="6" />
                <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
              </svg>
            </CredCircle>
            <CredLabel head="NOTARIZED" sub="All 50 States" />
          </CredBadge>
        </div>
      </div>
    </section>
  );
};

export default memo(CredentialsStrip);
